import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import { ReactNode } from "react";
import { radius } from "src/design/tokens";

export interface InlineAlertProps {
  severity: "info" | "warning" | "error" | "success";
  title?: string;
  children: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  /**
   * The design's page banner (e.g. "Mateo Silva can't be scored yet."): no icon, a 1.5 px
   * warningLine border, the bold title inline before the body and the action at the right.
   */
  banner?: boolean;
}

/** Soft-tone alert; the theme's Alert overrides supply background and ink. Always icon plus text. */
export const InlineAlert = ({
  severity,
  title,
  children,
  action,
  icon,
  banner = false,
}: InlineAlertProps) => {
  if (banner) {
    return (
      <Box
        role={
          severity === "error" || severity === "warning" ? "alert" : "status"
        }
        data-severity={severity}
        sx={(theme) => {
          const tone =
            severity === "warning"
              ? {
                  bg: theme.palette.tokens.warningBg,
                  fg: theme.palette.tokens.warningFg,
                  line: theme.palette.warningLine,
                }
              : severity === "error"
                ? {
                    bg: theme.palette.dangerSoft,
                    fg: theme.palette.dangerInk,
                    line: theme.palette.tokens.danger,
                  }
                : severity === "success"
                  ? {
                      bg: theme.palette.success.light,
                      fg: theme.palette.successInk,
                      line: theme.palette.success.main,
                    }
                  : {
                      bg: theme.palette.tokens.primarySoft,
                      fg: theme.palette.primaryInk,
                      line: theme.palette.divider,
                    };
          return {
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.75,
            p: "14px 18px",
            borderRadius: `${radius.panel}px`,
            bgcolor: tone.bg,
            color: tone.fg,
            border: `1.5px solid ${tone.line}`,
            fontSize: 14,
            fontWeight: 500,
          };
        }}
      >
        {title ? (
          <Box component="span" sx={{ fontWeight: 800 }}>
            {title}
          </Box>
        ) : null}
        <Box component="span" sx={{ flex: "1 1 280px" }}>
          {children}
        </Box>
        {action ? <Box sx={{ ml: "auto" }}>{action}</Box> : null}
      </Box>
    );
  }
  return (
    <Alert
      severity={severity}
      action={action}
      icon={icon}
      sx={{ alignItems: "center", "& .MuiAlert-message": { py: 1 } }}
    >
      {title ? <AlertTitle sx={{ fontWeight: 800 }}>{title}</AlertTitle> : null}
      {children}
    </Alert>
  );
};

export default InlineAlert;
